import type { BadgeTone } from '@/shared/components/ui/Badge';
import { tierOf, type AuthorityTier } from '@/shared/permissions/identity';
import type { LeaveStatus, LeaveType } from './types';

/**
 * Authority-tier presentation for the Team & Access directory. Derived from the
 * FROZEN Executive Authority Model — the UI never renders raw backend role
 * strings ("superadmin"/"admin"/"employee") or the literal "Super Admin".
 */
export const AUTHORITY_TIER_LABEL: Record<AuthorityTier, string> = {
  SUPER_ADMIN: 'Executive',
  ADMIN: 'Administrator',
  EMPLOYEE: 'Employee',
};

export const AUTHORITY_TIER_TONE: Record<AuthorityTier, BadgeTone> = {
  SUPER_ADMIN: 'primary',
  ADMIN: 'info',
  EMPLOYEE: 'neutral',
};

/** Tier-safe label for a backend role (never exposes the raw role). */
export function authorityLabel(role?: string | null): string {
  return AUTHORITY_TIER_LABEL[tierOf(role)];
}

export function authorityTone(role?: string | null): BadgeTone {
  return AUTHORITY_TIER_TONE[tierOf(role)];
}

export const LEAVE_TYPE_OPTIONS = [
  { value: 'annual', label: 'Annual' },
  { value: 'sick', label: 'Sick' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'other', label: 'Other' },
];

export const LEAVE_TYPE_LABELS: Record<string, string> = {
  annual: 'Annual',
  sick: 'Sick',
  emergency: 'Emergency',
  unpaid: 'Unpaid',
  other: 'Other',
};

export const LEAVE_STATUS_TONE: Record<LeaveStatus, BadgeTone> = {
  pending: 'warning',
  admin_approved: 'info',
  approved: 'success',
  rejected: 'danger',
};

export const LEAVE_STATUS_LABEL: Record<LeaveStatus, string> = {
  pending: 'Pending',
  admin_approved: 'Awaiting Executive',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  it: 'IT',
  digital_marketing: 'Digital Mktg',
  management: 'Management',
};

export function fmtDate(d?: string): string {
  if (!d) return '—';
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function isLeaveType(v: string): v is LeaveType {
  return ['annual', 'sick', 'emergency', 'unpaid', 'other'].includes(v);
}
